// src/pages/EventsPage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  FiCalendar,
  FiSearch,
  FiMapPin,
  FiExternalLink,
  FiThumbsUp,
  FiEye,
} from "react-icons/fi";
import "../styles/EventsPage.css";
import API from "../utils/axiosInstance";
import { useTranslation } from "react-i18next";

// -------- helpers --------
function ensureSessionKey() {
  if (typeof window === "undefined") return "";
  const k = localStorage.getItem("ev_sess");
  if (k) return k;
  const gen = crypto?.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);
  localStorage.setItem("ev_sess", gen);
  return gen;
}
const SESSION_KEY = ensureSessionKey();

function fmtDate(iso, lang) {
  try {
    const locale = lang?.startsWith("tr") ? "tr-TR" : "en-US";
    return new Date(iso).toLocaleDateString(locale, {
      year: "numeric",
      month: "long",
      day: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function EventsPage() {
  const { t, i18n } = useTranslation();

  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("newest"); // newest | oldest | likes
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const observersRef = useRef(new Map()); // id -> IO
  const pingedRef = useRef(new Set()); // pinglenen event id'leri

  const isLoggedIn =
    (typeof window !== "undefined" &&
      (localStorage.getItem("isLoggedIn") === "1" ||
        !!localStorage.getItem("accessToken"))) ||
    false;

  async function fetchEvents({ q = "", sort = "newest" } = {}) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (sort) params.set("sort", sort);

      // Eğer backend "lang" ile döndürüyorsa aç:
      // params.set("lang", i18n.language.startsWith("tr") ? "tr" : "en");

      const res = await API.get(`/api/events/?${params.toString()}`);

      const arr = (res.data || []).map((e) => ({
        ...e,
        liked: !!e.liked,
        likes_count: e.likes_count ?? 0,
        views_count: e.views_count ?? 0,
      }));
      setList(arr);
    } catch (err) {
      console.error("events fetch error:", err);
      setList([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const tt = setTimeout(() => {
      fetchEvents({ q: query, sort: sortKey });
    }, 250);
    return () => clearTimeout(tt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sortKey, i18n.language]);

  useEffect(() => {
    fetchEvents({ q: "", sort: "newest" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    observersRef.current.forEach((io) => io.disconnect());
    observersRef.current.clear();

    list.forEach((ev) => {
      const el = document.getElementById(`event-card-${ev.id}`);
      if (!el) return;
      if (pingedRef.current.has(ev.id)) return;

      const io = new IntersectionObserver(
        (entries) => {
          const [en] = entries;
          if (!en.isIntersecting) return;

          API.post(`/api/events/${ev.id}/view/`, { session_key: SESSION_KEY })
            .then((res) => {
              const v = res?.data?.views_count;
              if (typeof v === "number") {
                setList((prev) =>
                  prev.map((x) => (x.id === ev.id ? { ...x, views_count: v } : x))
                );
              } else {
                setList((prev) =>
                  prev.map((x) =>
                    x.id === ev.id
                      ? { ...x, views_count: (x.views_count ?? 0) + 1 }
                      : x
                  )
                );
              }
              pingedRef.current.add(ev.id);
            })
            .catch((err) => {
              console.error("event view ping error:", err);
            })
            .finally(() => {
              io.disconnect();
            });
        },
        { rootMargin: "200px 0px", threshold: 0.2 }
      );

      io.observe(el);
      observersRef.current.set(ev.id, io);
    });

    return () => {
      observersRef.current.forEach((io) => io.disconnect());
      observersRef.current.clear();
    };
  }, [list]);

  async function toggleLike(ev) {
    if (!isLoggedIn) {
      alert(t("events.alert.loginToLike"));
      return;
    }
    try {
      setList((prev) =>
        prev.map((x) =>
          x.id === ev.id
            ? {
                ...x,
                liked: !x.liked,
                likes_count: x.liked
                  ? Math.max(0, (x.likes_count ?? 0) - 1)
                  : (x.likes_count ?? 0) + 1,
              }
            : x
        )
      );

      const res = await API.post(`/api/events/likes/toggle/`, { event_id: ev.id });
      const { liked, likes_count } = res.data || {};
      setList((prev) =>
        prev.map((x) =>
          x.id === ev.id
            ? {
                ...x,
                liked: !!liked,
                likes_count: typeof likes_count === "number" ? likes_count : x.likes_count,
              }
            : x
        )
      );
    } catch (e) {
      setList((prev) =>
        prev.map((x) =>
          x.id === ev.id ? { ...x, liked: ev.liked, likes_count: ev.likes_count } : x
        )
      );
      if (e?.response?.status === 401) {
        alert(t("events.alert.loginToLike"));
      } else {
        console.error("event like error:", e);
        alert(t("events.alert.likeFailed"));
      }
    }
  }

  const rendered = useMemo(() => list, [list]);

  return (
    <div className="page events-page">
      <header className="events-header">
        <div className="events-title-row">
          <FiCalendar className="events-icon" />
          <h1 className="events-title">{t("events.title")}</h1>
        </div>

        <p className="events-subtitle">{t("events.subtitle")}</p>

        <div className="events-controls">
          <div className="sort-pill">
            <label htmlFor="sort" className="sort-label">
              {t("events.sort.label")}
            </label>
            <select
              id="sort"
              className="sort-select"
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value)}
              aria-label={t("events.sort.aria")}
            >
              <option value="newest">{t("events.sort.newest")}</option>
              <option value="oldest">{t("events.sort.oldest")}</option>
              <option value="likes">{t("events.sort.likes")}</option>
            </select>
          </div>
        </div>

        <form className="events-search" onSubmit={(e) => e.preventDefault()}>
          <FiSearch className="events-search-icon" />
          <input
            type="search"
            placeholder={t("events.search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t("events.search.aria")}
          />
        </form>
      </header>

      {loading && rendered.length === 0 ? (
        <div className="share__empty">{t("events.state.loading")}</div>
      ) : rendered.length === 0 ? (
        <div className="share__empty">{t("events.state.empty")}</div>
      ) : (
        <div className="events-grid">
          {rendered.map((ev) => (
            <article className="events-card" key={ev.id} id={`event-card-${ev.id}`}>
              <h2 className="events-title-text">{ev.title}</h2>

              <div className="events-meta">
                <span className="events-brand">{ev.brand}</span>
                <span className="events-dot">•</span>
                <time dateTime={ev.date}>
                  {fmtDate(ev.date, i18n.language)}{" "}
                  {ev.time ? <span className="events-time">({ev.time})</span> : null}
                </time>
              </div>

              <div className="events-stats">
                <button
                  type="button"
                  className={`stat btn-like ${ev.liked ? "liked" : ""}`}
                  onClick={() => toggleLike(ev)}
                  title={ev.liked ? t("events.card.unlike") : t("events.card.like")}
                >
                  <FiThumbsUp /> {ev.likes_count ?? 0}
                </button>

                <span className="stat" title={t("events.card.viewsTitle")}>
                  <FiEye /> {ev.views_count ?? 0}
                </span>
              </div>

              {ev.content ? <p className="events-summary">{ev.content}</p> : null}

              {(ev.city || ev.place) && (
                <div className="events-loc">
                  <FiMapPin />
                  <span>
                    {ev.city}
                    {ev.city && ev.place ? " • " : ""}
                    {ev.place}
                  </span>
                </div>
              )}

              {ev.cta_url ? (
                <div className="events-actions">
                  <a className="btn btn-cta" href={ev.cta_url} target="_blank" rel="noreferrer">
                    {(ev.cta_text || t("events.card.ctaFallback"))} <FiExternalLink />
                  </a>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}
    </div>
  );
}